import type { IndexProgress, Reading as HowItWasRead } from '../../api/search'

/**
 * The quiet line under the field: how MyLib read what was typed.
 *
 * It is the whole answer to "why did I not get what I expected", and it is there instead of an error
 * message. A search that refuses a line tells somebody their question was wrong; this shows them what
 * their question *became* — `Text: vertrag · Created: November 2025` — and lets them see for themselves
 * that the third word went in as text rather than as a filter.
 *
 * It stays put once anything has been typed. A line that appears only when something is wrong is a line
 * nobody reads when it matters, and one that comes and goes makes the whole surface jump.
 */
export interface ReadingProps {
  readonly reading: HowItWasRead | null
  /** How far the reading of the space has got. Said here as well, quietly, while anything is waiting. */
  readonly progress: IndexProgress | null
}

export function Reading({ reading, progress }: ReadingProps) {
  const outstanding = progress !== null && !progress.complete
  if (reading === null && !outstanding) {
    return null
  }

  const misread = reading !== null && reading.unreadable.length > 0

  return (
    <div className="reading">
      {reading !== null && (
        <p className="reading__line">
          {reading.text.trim() !== '' && (
            <span className="reading__part">
              Text: <b>{reading.text.trim()}</b>
            </span>
          )}
          {reading.filters.map((filter) => (
            <span className="reading__part" key={`${filter.key}-${filter.from}`}>
              {filter.label}: <b>{filter.reading}</b>
            </span>
          ))}
          {misread && (
            <span className="reading__part reading__part--text">
              {reading.unreadable.length === 1
                ? 'One word was not a filter, so it counts as text'
                : `${reading.unreadable.length} words were not filters, so they count as text`}
            </span>
          )}
        </p>
      )}

      {/*
        Said in both surfaces, in the same words, so that an incomplete result is never read as a
        complete one — and said quietly, because it corrects itself within seconds.
      */}
      {outstanding && progress !== null && (
        <p className="reading__waiting" role="status">
          {progress.outstanding === 1
            ? '1 document is still being read, so it cannot be found by its content yet.'
            : `${progress.outstanding} documents are still being read, so they cannot be found by their content yet.`}
        </p>
      )}
    </div>
  )
}
